import cn from 'classnames';
import { forwardRef } from 'react';
import { brand } from '@/brand';

// Поле гостевой формы. Вид — по раскладке (brand.layout.auth, см. AuthLayout):
// 'split' — подпись стоит плейсхолдером внутри поля (прежний вид); 'card' —
// подпись мелкими заглавными над полем, в плейсхолдере пример значения
// («name@company.com»), как в макете telecom.

type AuthInputProps = Omit<
  React.DetailedHTMLProps<React.InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>,
  'placeholder'
> & {
  // Что за поле: «Email», «Пароль».
  label: string;
  // Пример значения — плейсхолдер в виде 'card'. В 'split' не показывается:
  // плейсхолдер там занят подписью.
  example?: string;
  error?: string;
  showError?: boolean;
};

export const AuthInput = forwardRef<HTMLInputElement, AuthInputProps>(
  ({ className, label, example, error, showError, ...props }, ref) => {
    if (brand.layout.auth === 'card') {
      return (
        <label className="flex w-full flex-col gap-1.5">
          <span className="text-[11px] font-semibold uppercase tracking-[0.06em] text-content-auth-secondary">
            {label}
          </span>
          <input
            {...props}
            ref={ref}
            placeholder={example}
            className={cn(
              'w-full rounded-control border-[1.5px] bg-surface-auth-input px-3.5 py-3 text-sm text-content-auth-input placeholder-content-auth-placeholder outline-none',
              showError ? 'border-line-danger focus:border-line-danger' : 'border-line-auth-input focus:border-line-auth-input-focus',
              className,
            )}
          />
          {error && <span className="text-xs text-content-error">{error}</span>}
        </label>
      );
    }

    const inputClassName = cn(
      'rounded-control border-line-auth-input bg-surface-auth-input text-content-auth-input placeholder-content-auth-placeholder w-full border px-[11px] py-[9px] outline-none',
      className,
      showError
        ? 'border-line-danger focus:border-line-danger'
        : 'focus:border-line-auth-input-focus'
    );

    return (
      <div className="w-full">
        <input {...props} placeholder={label} className={inputClassName} ref={ref} />
        {error && <p className="text-content-error mt-0.5 pl-3 text-xs">{error}</p>}
      </div>
    );
  }
);

// forwardRef-компонент не получает имя автоматически: без displayName он виден в
// React DevTools и в ошибках как ForwardRef, а eslint (react/display-name) падает.
AuthInput.displayName = 'AuthInput';
